import React from 'react';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { FlatButton } from '@ui/buttons';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import Delete from '@assets/icons/svgs/Delete';
import { Edit } from '@assets/icons/svgs/Edit';
import { useDispatch } from 'react-redux';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { AI_TOOLS, UPLOAD_DOC_TYPE_OPTIONS } from '@constants';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { TagSearchInput } from '@features/query/form/TagInput';
import { ModeContext } from '@/contexts/ModeContext';
import { liveDocsActions } from '@constants/actionTypes';
import { parseDocGroup } from '@utils/doc';
import { ROUTES } from '@constants/routes';
import { CloseButton } from '@ui/buttons/CloseButton';
import { showErrorBanner } from '@utils/application';

const DetailViewWrapper = styled.div`
  padding: 16px 36px;
  margin-bottom: 13px;

  * {
    word-break: break-all;
  }
`;
const FieldsWrapper = styled.div`
  display: grid;
  grid-template-columns: 120px calc(100% - 120px);
  gap: 8px;
`;
const ActionsWrapper = styled.div`
  padding: 12px 0 0 0;

  display: flex;
  flex-direction: row;
  gap: 16px;

  button {
    min-width: 200px;
  }
`;
const SaveButtonWrapper = styled.div`
  button {
    padding: 6px 58px;
  }
`;
const ViewOriginalButtonWrapper = styled.div`
  button {
    padding: 6px 24px;
  }
`;
const DeleteButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  button {
    padding: 6px 16px;

    border-color: ${({ theme }) => theme.themeColors.error} !important;

    * {
      color: ${({ theme }) => theme.themeColors.error} !important;
    }

    &:hover {
      background-color: ${({ theme }) => theme.themeColors.error} !important;
      color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;

      * {
        color: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;
      }
    }
  }

  button:hover {
    * {
      color: ${({ theme }) => theme.themeColors.text} !important;
    }
  }
`;

const EditButtonWrapper = styled.div`
  height: 26px;
  padding: 5px 8px 0 24px;

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const WordWrap = styled.div`
  word-break: break-all;
  word-wrap: anywhere;
`;
const DeleteFieldButtonWrapper = styled.div`
  height: 26px;
  padding: 5px 8px 0 8px;

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const FieldName = styled.div`
  cursor: pointer;
`;
const FieldValue = styled.div`
  width: 100%;
  cursor: pointer;
`;

const AccessGroupsRowValue = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;
const AccessGroupPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.12)};
  color: ${({ theme }) => theme.themeColors.primaryCtaButton};
`;

const ReadOnlyFieldName = styled.div`
  padding-top: 2px;
`;

const Select = styled.select`
  outline: none;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};

  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.themeColors.text};
  margin: 0;
  padding: 4px 8px;
  min-width: 300px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.6)};
  }
`;

const Input = styled.input`
  outline: none;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};

  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.themeColors.text};
  margin: 0;
  padding: 4px 8px;
  min-width: 300px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.6)};
  }
`;
const TagSearchInputWrapper = styled.div`
  input {
    outline: none;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.themeColors.text};
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.themeColors.text};
    margin: 0;
    padding: 4px 8px;
    min-width: 300px;
    width: 100%;

    &::placeholder {
      color: ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.6)};
    }
  }
`;
const SavedMessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  min-width: 288px;

  margin-top: 8px;

  padding: 8px 12px 8px 24px;
  border-radius: 5px;
  background: ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};

  opacity: 0.8;

  * {
    color: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;
const CloseButtonWrapper = styled.div`
  padding: 0 8px 0 0;

  svg {
    * {
      fill: ${({ theme }) => theme.themeColors.componentNoOpacity} !important;
    }
  }
`;

const DeleteButton = ({ onClick, value }) => {
  const hc = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  if (value === '') return null;
  return (
    <DeleteFieldButtonWrapper onClick={hc}>
      <Delete />
    </DeleteFieldButtonWrapper>
  );
};
const EditButton = ({ onClick, isEditting }) => {
  if (isEditting) return <div />;

  return (
    <EditButtonWrapper onClick={onClick}>
      <Edit />
    </EditButtonWrapper>
  );
};

const AutoFocusTickerSearch = ({ onAddTicker }) => {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const onAddTag = (t) => {
    onAddTicker(t?.symbol || '');
  };
  return (
    <TagSearchInputWrapper>
      <ModeContext.Provider value={{}}>
        <TagSearchInput forwardRef={inputRef} onAddTag={onAddTag} placeholder="Search ticker" />
      </ModeContext.Provider>
    </TagSearchInputWrapper>
  );
};

const AutoFocusTagSearch = ({ onAddTag }) => {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);
  return (
    <TagSearchInputWrapper>
      <ModeContext.Provider value={{ onlyCompanyTags: true, showCompanyTags: true }}>
        <TagSearchInput forwardRef={inputRef} onAddTag={onAddTag} enableCreateTag placeholder="Search company / tag" />
      </ModeContext.Provider>
    </TagSearchInputWrapper>
  );
};

const InputField = ({ keyValue, value, onChange, onBlur }) => {
  if (keyValue === 'doc_type') {
    return (
      <Select value={value} onChange={onChange} onBlur={onBlur}>
        <option value={'unknown'}>Optional...</option>
        {UPLOAD_DOC_TYPE_OPTIONS.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </Select>
    );
  }
  if (keyValue === 'security_symbol') {
    const onAddTicker = (v) => {
      onChange(v);
      setTimeout(() => {
        onBlur();
      }, 100);
    };
    return <AutoFocusTickerSearch onAddTicker={onAddTicker} />;
  }
  if (keyValue === 'company_tag') {
    const onAddTag = (t) => {
      onChange(t?.doc_tag_id || t?.id || '', t?.name || '');
      setTimeout(() => {
        onBlur();
      }, 100);
    };
    return <AutoFocusTagSearch onAddTag={onAddTag} />;
  }
  if (keyValue === 'quarter') {
    return (
      <Select value={value} onChange={onChange} onBlur={onBlur}>
        <option value="">Select Quarter...</option>
        <option value="Q1">Q1</option>
        <option value="Q2">Q2</option>
        <option value="Q3">Q3</option>
        <option value="Q4">Q4</option>
      </Select>
    );
  }
  if (keyValue === 'month') {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return (
      <Select value={value} onChange={onChange} onBlur={onBlur}>
        <option value="">Select Month...</option>
        {months.map((month) => (
          <option key={month} value={month}>
            {month}
          </option>
        ))}
      </Select>
    );
  }
  if (keyValue === 'year') {
    const currentYear = 2026;
    const years = Array.from({ length: 25 }, (_, i) => currentYear - i);
    return (
      <Select value={value} onChange={onChange} onBlur={onBlur}>
        <option value="">Select Year...</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </Select>
    );
  }

  return <Input value={value} onChange={onChange} onBlur={onBlur} autoFocus />;
};

const Field = ({ keyName, keyValue, value, onChange, onDelete }) => {
  const [isEditting, setIsEditting] = React.useState(false);

  const handleEditStart = () => {
    setIsEditting(true);
  };
  const onBlur = () => {
    setIsEditting(false);
  };

  return (
    <>
      <FieldName onClick={handleEditStart}>
        <Container right={20} row verticallyCenter height={26}>
          <Body7>{keyName}:</Body7>
        </Container>
      </FieldName>
      <FieldValue onClick={handleEditStart}>
        {isEditting ? (
          <InputField value={value} onChange={onChange} keyValue={keyValue} onBlur={onBlur} />
        ) : (
          <WordWrap>
            <Container row verticallyCenter>
              <Body7>{formatDisplayValue(value)}</Body7>
              <EditButton isEditting={isEditting} onClick={handleEditStart} />
              {keyValue === 'company_tag' ? <DeleteButton onClick={onDelete} value={value} /> : null}
            </Container>
          </WordWrap>
        )}
      </FieldValue>
    </>
  );
};

const formatDisplayValue = (v) => {
  if (v === 'unknown') return 'Unassigned';

  return v ? v : 'Unassigned';
};

function testAreUnsavedChanges(data, form) {
  let are = false;
  if (data.filename !== form.filename) {
    are = true;
  }
  if (data.security_symbol !== form.ticker) {
    are = true;
  }
  if ((data.doc_tag ? data.doc_tag.name : '') !== form.companyTag) {
    are = true;
  }
  if (data.doc_type !== form.docType) {
    are = true;
  }
  if (data.fiscal_quarter !== form.fiscal_quarter) {
    are = true;
  }
  if (data.fiscal_month !== form.fiscal_month) {
    are = true;
  }
  if (data.fiscal_year !== form.fiscal_year) {
    are = true;
  }
  return are;
}

const convertQuarter = (quarter) => {
  if (quarter === 'Q1') return 1;
  if (quarter === 'Q2') return 2;
  if (quarter === 'Q3') return 3;
  if (quarter === 'Q4') return 4;
  return null;
};
const convertMonth = (month) => {
  if (month === 'Jan') return 1;
  if (month === 'Feb') return 2;
  if (month === 'Mar') return 3;
  if (month === 'Apr') return 4;
  if (month === 'May') return 5;
  if (month === 'Jun') return 6;
  if (month === 'Jul') return 7;
  if (month === 'Aug') return 8;
  if (month === 'Sep') return 9;
  if (month === 'Oct') return 10;
  if (month === 'Nov') return 11;
  if (month === 'Dec') return 12;
  return null;
};
const convertYear = (year) => {
  if (!year) return null;
  return parseInt(year, 10);
};

export const FileDetail = ({ data, onDeleteClick }) => {
  const dispatch = useDispatch();
  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCompanyTaggingEnabled = toolsAvailable.includes(AI_TOOLS.COMPANY_TAGGING) || true;

  const [saving, setSaving] = React.useState(false);
  const [filename, setFilename] = React.useState(data.filename);
  const [ticker, setTicker] = React.useState(data.security_symbol);
  const [companyTagId, setCompanyTagId] = React.useState(data.doc_tag ? data.doc_tag.id : '');
  const [companyTag, setCompanyTag] = React.useState(data.doc_tag ? data.doc_tag.name : '');
  const [docType, setDocType] = React.useState(data.doc_type);
  const [quarter, setQuarter] = React.useState(data.fiscal_quarter);
  const [month, setMonth] = React.useState(data.fiscal_month);
  const [year, setYear] = React.useState(data.fiscal_year);
  const unsavedChanges = testAreUnsavedChanges(data, { filename, ticker, companyTag, docType, quarter, month, year });

  const [showSavedMessage, setShowSavedMessage] = React.useState(false);

  React.useEffect(() => {
    if (showSavedMessage) {
      setTimeout(() => {
        setShowSavedMessage(false);
      }, 2400);
    }
  }, [showSavedMessage]);

  const handleFilenameChange = (e) => {
    const value = e.target.value;
    setFilename(value);
  };

  const handleTickerChange = (v) => {
    setTicker(v);
  };
  const handleQuarterChange = (e) => {
    const value = e.target.value;
    setQuarter(value);
  };
  const handleMonthChange = (e) => {
    const value = e.target.value;
    setMonth(value);
  };
  const handleYearChange = (e) => {
    const value = e.target.value;
    setYear(value);
  };
  const handleCompanyTagChange = (id, name) => {
    setCompanyTagId(id);
    setCompanyTag(name);
  };
  const handleDeleteCompanyTag = () => {
    setCompanyTagId('');
    setCompanyTag('');
  };
  const handleDocumentTypeChange = (e) => {
    const value = e.target.value;
    setDocType(value);
  };
  const onHideSavedMessage = () => {
    setShowSavedMessage(false);
  };

  const handleViewOriginal = () => {
    const { id } = data;
    window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
  };

  const handleQueryFile = () => {
    const { id } = data;
    const url = ROUTES.digitalAnalyst + `?doc-ids=${id}`;
    window.open(url, '_blank');
  };

  const handleSave = async () => {
    setSaving(true);
    const { id } = data;
    const oldGroupName = parseDocGroup(data);
    const form = {
      name: filename,
      ticker,
      doc_tag_id: companyTagId,
      fiscal_quarter: convertQuarter(quarter),
      fiscal_month: convertMonth(month),
      fiscal_year: convertYear(year),
    };
    if (docType !== 'unknown') {
      form.doc_type = docType;
    }
    const URL = 'v1/genai_user_doc/' + id;
    const { status, data: updatedServerDoc, error } = await sendApiRequest('PATCH', URL, form);
    logNetRequest(status, error);
    handle400Statuses(status);
    setSaving(false);

    if (status === 200) {
      dispatch({
        type: liveDocsActions.editDoc,
        payload: { id, groupName: oldGroupName, updatedDoc: updatedServerDoc },
      });
      setShowSavedMessage(true);
    } else {
      const message = error || 'Something went wrong, please try again.';
      showErrorBanner(message, {}, dispatch);
    }
  };

  const handleReset = () => {
    setFilename(data.filename);
    setTicker(data.security_symbol);
    setCompanyTag(data.doc_tag ? data.doc_tag.name : '');
    setDocType(data.doc_type);
    setQuarter(data.fiscal_quarter);
    setMonth(data.fiscal_month);
    setYear(data.fiscal_year);
  };

  return (
    <DetailViewWrapper>
      <FieldsWrapper>
        <Field keyName={'Filename'} keyValue={'filename'} value={filename} onChange={handleFilenameChange} />
        <>
          <ReadOnlyFieldName>
            <Container right={20} row verticallyCenter height={26}>
              <Body7>Access Groups:</Body7>
            </Container>
          </ReadOnlyFieldName>
          <AccessGroupsRowValue>
            {data.access_groups?.length > 0 ? (
              data.access_groups.map((group) => <AccessGroupPill key={group.id}>{group.name}</AccessGroupPill>)
            ) : (
              <Body7 isLowContrast>None</Body7>
            )}
          </AccessGroupsRowValue>
        </>
        <Field keyName={'Document Type'} keyValue={'doc_type'} value={docType} onChange={handleDocumentTypeChange} />
        <Field keyName={'Ticker'} keyValue={'security_symbol'} value={ticker} onChange={handleTickerChange} />
        {isCompanyTaggingEnabled && (
          <Field
            keyName={'Tag'}
            keyValue={'company_tag'}
            value={companyTag}
            onChange={handleCompanyTagChange}
            onDelete={handleDeleteCompanyTag}
          />
        )}
        <Field keyName={'Quarter'} keyValue={'quarter'} value={quarter} onChange={handleQuarterChange} />
        <Field keyName={'Month'} keyValue={'month'} value={month} onChange={handleMonthChange} />
        <Field keyName={'Year'} keyValue={'year'} value={year} onChange={handleYearChange} />
      </FieldsWrapper>
      {showSavedMessage && (
        <SavedMessageWrapper top={4}>
          <Body5 bold>Updates were saved.</Body5>
          <CloseButtonWrapper>
            <CloseButton size={12} onClick={onHideSavedMessage}></CloseButton>
          </CloseButtonWrapper>
        </SavedMessageWrapper>
      )}
      <ActionsWrapper>
        {saving ? (
          <SaveButtonWrapper>
            <FlatButton onClick={() => null} disabled>
              Saving...
            </FlatButton>
          </SaveButtonWrapper>
        ) : unsavedChanges ? (
          <>
            <SaveButtonWrapper>
              <FlatButton onClick={handleSave}>Save</FlatButton>
            </SaveButtonWrapper>
            <SaveButtonWrapper>
              <SkeletonButton onClick={handleReset}>Reset</SkeletonButton>
            </SaveButtonWrapper>
          </>
        ) : null}
      </ActionsWrapper>

      {onDeleteClick && (
        <ActionsWrapper>
          <DeleteButtonWrapper>
            <SkeletonButton onClick={onDeleteClick}>Delete File</SkeletonButton>
          </DeleteButtonWrapper>
        </ActionsWrapper>
      )}

      {data.blob_id && (
        <ActionsWrapper>
          <ViewOriginalButtonWrapper>
            <SkeletonButton onClick={handleViewOriginal}>View Original File</SkeletonButton>
          </ViewOriginalButtonWrapper>
        </ActionsWrapper>
      )}

      <ActionsWrapper>
        <ViewOriginalButtonWrapper>
          <SkeletonButton onClick={handleQueryFile}>Query File</SkeletonButton>
        </ViewOriginalButtonWrapper>
      </ActionsWrapper>
    </DetailViewWrapper>
  );
};
